import { Calendar, DateNavigator } from "common/components";
import { Column } from "common/components/Layout/new/constLayout";
import { useCallback, useEffect, useState } from "react";
import { MonthYearPicker } from "./MonthYearPicker";
import styled from "styled-components";

export const DATE_PICKER_MODE = {
  CALENDAR: "CALENDAR",
  MONTH: "MONTH",
};

export const DatePicker = ({
  date: initDate,
  minDate,
  maxDate,
  onChangeYear,
  onChangeMonth,
  onSelect,
  initialMode = DATE_PICKER_MODE.CALENDAR,
  fixedMode = false,
  disabledDate,
}) => {
  const [currentDate, setCurrentDate] = useState(initDate || new Date());
  const [calendarMode, setCalendarMode] = useState(
    initialMode === DATE_PICKER_MODE.CALENDAR,
  );

  useEffect(() => {
    if (initDate) {
      setCurrentDate(initDate);
    }
  }, [initDate]);

  const goToPrevYear = useCallback(() => {
    const newDate = new Date(
      currentDate.getFullYear() - 1,
      currentDate.getMonth(),
      1,
    );
    setCurrentDate(newDate);
    onChangeYear?.(newDate);
  }, [currentDate, onChangeYear]);

  const goToNextYear = useCallback(() => {
    const newDate = new Date(
      currentDate.getFullYear() + 1,
      currentDate.getMonth(),
      1,
    );
    setCurrentDate(newDate);
    onChangeYear?.(newDate);
  }, [currentDate, onChangeYear]);

  const goToPrevMonth = useCallback(() => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    );
    setCurrentDate(newDate);
    onChangeMonth?.(newDate);
  }, [currentDate, onChangeMonth]);

  const goToNextMonth = useCallback(() => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1,
    );
    setCurrentDate(newDate);
    onChangeMonth?.(newDate);
  }, [currentDate, onChangeMonth]);

  const handleCalendarSelect = useCallback(
    (date) => {
      setCurrentDate(date);
      onSelect?.(date);
    },
    [onSelect],
  );

  const handleMonthYearSelect = useCallback(
    (date) => {
      setCurrentDate(date);

      if (initialMode === DATE_PICKER_MODE.MONTH) {
        onSelect?.(date);
      }

      if (!fixedMode) {
        onChangeYear?.(date);
        onChangeMonth?.(date);
        setCalendarMode(true);
      }
    },
    [initialMode, fixedMode, onChangeYear, onChangeMonth, onSelect],
  );

  const toggleMode = useCallback(() => {
    if (!fixedMode) {
      setCalendarMode((prev) => !prev);
    }
  }, [fixedMode]);

  return (
    <Column>
      <DateNavigator
        date={currentDate}
        format={calendarMode ? "YYYY.MM" : "YYYY"}
        onClick={toggleMode}
        onPrev={calendarMode ? goToPrevMonth : goToPrevYear}
        onNext={calendarMode ? goToNextMonth : goToNextYear}
        style={{ width: "100%" }}
      />
      <PickerBody>
        {calendarMode ? (
          <Calendar
            currentDate={currentDate}
            startDate={minDate || initDate}
            endDate={maxDate}
            onSelect={handleCalendarSelect}
            onPrevMonth={goToPrevMonth}
            onNextMonth={goToNextMonth}
            disabledDate={disabledDate}
          />
        ) : (
          <MonthYearPicker
            date={currentDate}
            minDate={minDate}
            maxDate={maxDate}
            onSelect={handleMonthYearSelect}
          />
        )}
      </PickerBody>
    </Column>
  );
};

const PickerBody = styled.div`
  width: 26rem;
  height: 26rem;
`;
